// Screen 14 · Landing Board — plan, load and capacity.
// Answers: when does each job land, how many hours of which kind does it take, and does the
// shop have room, before I commit a date or a hire?
// Hours come from the resolved job, by labor class and by work center. Possible jobs
// reserve hours in proportion to their likelihood. Capacity is a setting edited here.

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCrumb } from '../frame/Frame';
import { insertRow, updateRow, useRows, type Row } from '../lib/api';
import { capacityHours, hoursInWindow, readCapacity, weight, type Capacity } from '../lib/capacity';
import { longDate, shortDate, today } from '../lib/format';
import { jobReadiness, useInventory, type InventoryData } from '../lib/inventory';
import { jobTitle, useAllJobs, type JobSummary } from '../lib/jobs';
import { useSession } from '../lib/session';
import { Btn, Edit, Empty, Head, Info, Ln, Loading, Money, Panel, Seg } from '../ui/kit';

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function months(count: number): { label: string; start: string; end: string }[] {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const a = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const b = new Date(now.getFullYear(), now.getMonth() + i + 1, 0);
    return { label: a.toLocaleDateString('en-US', { month: 'short' }), start: iso(a), end: iso(b) };
  });
}

const likely = (j: JobSummary) => (j.opp?.outcome === 'won' ? 'Signed' : j.opp?.likelihood_pct !== null && j.opp?.likelihood_pct !== undefined ? `${Number(j.opp.likelihood_pct)}%` : '—');

export function Plan() {
  useCrumb([{ label: 'Plan' }]);
  const s = useSession();
  const all = useAllJobs();
  const settings = useRows('setting');
  const [params, setParams] = useSearchParams();
  const tab = params.get('view') ?? 'plan';
  const capRow = settings.rows.find((x) => x.key === 'capacity');
  const cap = readCapacity(capRow?.value);
  const ms = useMemo(() => months(6), []);
  const inv = useInventory();
  if (all.loading) return <Loading what="Loading the plan" />;
  const live = all.jobs.filter((j) => j.opp?.outcome !== 'lost' && j.opp?.outcome !== 'not_a_lead');

  return (
    <div>
      <Head title="Landing Board" sub="When each job lands, the hours it takes, and whether the shop has room.">
        <Seg
          items={[{ key: 'plan', label: 'Plan' }, { key: 'load', label: 'Load' }, { key: 'capacity', label: 'Capacity' }, { key: 'pipeline', label: 'Pipeline' }]}
          value={tab}
          onChange={(k) => setParams({ view: k }, { replace: true })}
        />
      </Head>

      {tab === 'plan' && (
        <Panel label="Jobs by month · a bar is the build window, the date is the anchor">
          {live.length === 0 ? (
            <Empty>No jobs yet.</Empty>
          ) : (
            <div className="tblwrap">
              <table>
                <thead>
                  <tr>
                    <th>Job</th>
                    {ms.map((m) => (
                      <th key={m.start}>{m.label}</th>
                    ))}
                    <th className="right">Hours</th>
                    <th>Likely</th>
                    <th>
                      Materials
                      <Info left>
                        How soon everything the job still needs could be in hand: each material not yet ordered at its lead time from today, each one on order at its expected date. A build cannot start before the longest of them. "Lock by" is the last day the job
                        can lock (and its materials be ordered) and still have them for the build window. A job whose materials are all local and quick can be slotted sooner. Lead times are set on the vendor, or on the material where it differs.
                      </Info>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {live.map((j) => (
                    <tr key={j.job.id}>
                      <td>
                        <Link to={j.opp ? `/opportunities/${j.opp.id}` : `/jobs/${j.job.id}`}>
                          <b>{jobTitle(j)}</b>
                        </Link>
                        {!j.opp?.window_start && (
                          <div>
                            <small>no build window yet</small>
                          </div>
                        )}
                      </td>
                      {ms.map((m) => {
                        const inWindow = j.opp?.window_start && j.opp?.window_end && j.opp.window_start <= m.end && j.opp.window_end >= m.start;
                        const anchor = j.opp?.anchor_date && j.opp.anchor_date >= m.start && j.opp.anchor_date <= m.end;
                        return (
                          <td key={m.start}>
                            {inWindow && <div style={{ height: 10, background: 'var(--accent)', opacity: weight(j) < 1 ? 0.45 : 1 }} title="build window" />}
                            {anchor && (
                              <small>
                                {j.opp!.anchor_type ?? 'anchor'} {shortDate(j.opp!.anchor_date)}
                              </small>
                            )}
                          </td>
                        );
                      })}
                      <td className="right num">{Number(j.res?.totals.labor_hours ?? 0).toFixed(1)}</td>
                      <td>{likely(j)}</td>
                      <td>
                        <Readiness inv={inv} j={j} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <small>The window is set on the opportunity, or on the job's Builds tab. It is one schedule shown in two places.</small>
        </Panel>
      )}

      {tab === 'load' && <Load jobs={live} ms={ms} cap={cap} />}
      {tab === 'capacity' && <CapacityEditor row={capRow} cap={cap} canEdit={s.canEdit('screen.landing_board')} />}

      {tab === 'pipeline' && (
        <Panel label={`Pipeline (${live.length})`}>
          {live.map((j) => (
            <Ln
              key={j.job.id}
              left={
                <>
                  <Link to={`/jobs/${j.job.id}`}>
                    <b>{jobTitle(j)}</b>
                  </Link>
                  <br />
                  <small>
                    {j.stage?.name ?? 'no step'} · {j.opp?.anchor_type ? `${j.opp.anchor_type} ${longDate(j.opp.anchor_date)}` : 'no anchor'}
                  </small>
                </>
              }
              right={
                <>
                  {likely(j)} · <Money cents={j.res?.totals.price_cents} whole />
                </>
              }
            />
          ))}
          {live.length === 0 && <Empty>No jobs yet.</Empty>}
        </Panel>
      )}
    </div>
  );
}

/**
 * Lead times flow forward to the schedule: the earliest the job's materials can all be in
 * hand, which one sets that date, and the last day to lock (or order) and still hold the
 * build window.
 */
function Readiness({ inv, j }: { inv: InventoryData; j: JobSummary }) {
  if (inv.loading) return <small>…</small>;
  const r = jobReadiness(inv, j);
  const name = (id: string) => inv.materialById.get(id)?.description ?? 'a material';
  const start = j.opp?.window_start ? String(j.opp.window_start).slice(0, 10) : null;
  if (r.outstanding === 0) return <small>{j.tier === 'firm' || j.tier === 'committed' ? 'all in hand' : 'nothing to order'}</small>;
  const verb = j.tier === 'firm' ? 'Order' : 'Lock';
  const pastDue = r.order_by !== null && r.order_by < today();
  return (
    <div style={{ minWidth: 190 }}>
      {r.earliest_start ? (
        <div className={r.late ? 'warn' : ''}>
          in hand by <b>{shortDate(r.earliest_start)}</b>
          {r.late && start && ` · after the window opens ${shortDate(start)}`}
        </div>
      ) : (
        <div className="warn">cannot be dated</div>
      )}
      {r.longest && (
        <small>
          longest: {name(r.longest.material_id)}
          {r.longest.lead_days !== null ? ` (${r.longest.lead_days} days)` : ' (on order)'}
        </small>
      )}
      {r.order_by && (
        <div>
          <small className={pastDue ? 'warn' : ''}>
            {verb} by {shortDate(r.order_by)} to hold {shortDate(start)}
            {pastDue && ': that day has passed'}
          </small>
        </div>
      )}
      {r.unknown.length > 0 && (
        <div>
          <small title={r.unknown.map(name).join(', ')}>
            {r.unknown.length} of {r.outstanding} with no lead time
          </small>
        </div>
      )}
    </div>
  );
}

function Load({ jobs, ms, cap }: { jobs: JobSummary[]; ms: ReturnType<typeof months>; cap: Capacity | null }) {
  const all = useAllJobs();
  const lib = all.lib;
  return (
    <div className="stack">
      {!cap && (
        <Panel soft>
          The shop's people and hours have not been entered yet, so load is shown without a capacity line. Enter them on the Capacity tab.
        </Panel>
      )}
      <div className="tiles" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
        {ms.map((m) => {
          const byClass = new Map<string, number>();
          const byCenter = new Map<string, number>();
          let total = 0;
          for (const j of jobs) {
            const share = Number(j.res?.totals.labor_hours ?? 0) > 0 ? hoursInWindow(j, m.start, m.end) / Number(j.res!.totals.labor_hours) : 0;
            if (share <= 0 || !j.res) continue;
            const w = weight(j) * share;
            for (const [cls, h] of Object.entries(j.res.totals.hours_by_class)) byClass.set(cls, (byClass.get(cls) ?? 0) + Number(h) * w);
            for (const g of j.res.groups) for (const st of g.steps) byCenter.set(st.work_center_id ?? 'none', (byCenter.get(st.work_center_id ?? 'none') ?? 0) + (Number(st.minutes) / 60) * w);
            total += Number(j.res.totals.labor_hours) * w;
          }
          const have = cap ? capacityHours(cap, m.start, m.end) : null;
          const over = have !== null ? total - have : 0;
          return (
            <div key={m.start} className="tile">
              <div className="lb">
                <span>Load · {m.label}</span>
                {have !== null && <span>{over > 0 ? <span className="warn">+{over.toFixed(0)} h</span> : `${(have - total).toFixed(0)} h open`}</span>}
              </div>
              <div className="big">
                {total.toFixed(0)}
                {have !== null ? ` of ${have.toFixed(0)}` : ''} h
              </div>
              {have !== null && (
                <div className="meter">
                  <i className={over > 0 ? 'over' : ''} style={{ width: `${Math.min(100, have > 0 ? (total / have) * 100 : 100)}%` }} />
                </div>
              )}
              {[...byClass.entries()].filter(([, h]) => h > 0.05).map(([cls, h]) => (
                <Ln key={cls} left={lib?.laborClasses[cls]?.name ?? 'No class'} right={`${h.toFixed(1)} h`} />
              ))}
              {[...byCenter.entries()].filter(([, h]) => h > 0.05).map(([id, h]) => (
                <Ln key={id} sub left={`at ${lib?.workCenters[id]?.name ?? 'no station'}`} right={`${h.toFixed(1)} h`} />
              ))}
              {total === 0 && <small>Nothing lands here.</small>}
            </div>
          );
        })}
      </div>
      <small>
        A job's hours are spread evenly across its build window and weighted by its likelihood (a signed job counts in full).
        <Info>Hours come from the resolved job by labor class and work center, not from size defaults.</Info>
      </small>
    </div>
  );
}

function CapacityEditor({ row, cap, canEdit }: { row: Row | undefined; cap: Capacity | null; canEdit: boolean }) {
  const { user } = useSession();
  const value: Capacity = cap ?? { people: [], efficiency: 1, blocks: [] };
  const save = async (next: Capacity) => {
    if (row) await updateRow('setting', row.id, { value: next, updated_by: user?.id ?? null });
    else await insertRow('setting', { key: 'capacity', label: 'Shop capacity: people, hours, blocked time, efficiency', group: 'Plan', value: next, updated_by: user?.id ?? null });
  };
  return (
    <div className="row">
      <div className="col">
        <Panel
          label={`People (${value.people.length})`}
          right={canEdit ? <Btn small onClick={() => void save({ ...value, people: [...value.people, { name: 'New person', hours_per_week: 40, starts: null }] })}>+ Person or hire</Btn> : undefined}
        >
          {value.people.length === 0 && <Empty>No one entered yet. Add each person, or a planned hire with a start date, and the hours a week they build.</Empty>}
          {value.people.map((p, i) => {
            const patch = (x: Partial<Capacity['people'][number]>) => save({ ...value, people: value.people.map((q, k) => (k === i ? { ...q, ...x } : q)) });
            return (
              <Ln
                key={i}
                left={<Edit className="cell" disabled={!canEdit} value={p.name} onSave={(v) => patch({ name: v || p.name })} />}
                right={
                  <span className="nowrap">
                    <Edit className="cell short right" type="number" disabled={!canEdit} value={p.hours_per_week} onSave={(v) => patch({ hours_per_week: Number(v) || 0 })} /> h/week · starts{' '}
                    <Edit className="cell" type="date" disabled={!canEdit} value={p.starts ?? ''} onSave={(v) => patch({ starts: v || null })} />{' '}
                    {canEdit && <Btn small title="Remove this person" onClick={() => window.confirm(`Remove ${p.name} from the shop's capacity?`) && void save({ ...value, people: value.people.filter((_, k) => k !== i) })}>×</Btn>}
                  </span>
                }
              />
            );
          })}
        </Panel>
      </div>
      <div className="col stack">
        <Panel label="Efficiency factor" right={<Info left>The share of paid hours that land on a job. 0.85 means 34 of 40 hours.</Info>}>
          <Edit className="short" disabled={!canEdit} value={value.efficiency} parse={(t) => (/^0?\.\d+$|^1(\.0+)?$/.test(t.trim()) ? t.trim() : undefined)} onSave={(v) => save({ ...value, efficiency: Number(v) })} />
        </Panel>
        <Panel label={`Blocked time (${value.blocks.length})`} right={canEdit ? <Btn small onClick={() => void save({ ...value, blocks: [...value.blocks, { start: iso(new Date()), end: iso(new Date()), note: '' }] })}>+ Block</Btn> : undefined}>
          {value.blocks.length === 0 && <Empty>No blocked time.</Empty>}
          {value.blocks.map((b, i) => {
            const patch = (x: Partial<Capacity['blocks'][number]>) => save({ ...value, blocks: value.blocks.map((q, k) => (k === i ? { ...q, ...x } : q)) });
            return (
              <Ln
                key={i}
                left={<Edit className="cell" disabled={!canEdit} value={b.note ?? ''} placeholder="what it is" onSave={(v) => patch({ note: v })} />}
                right={
                  <span className="nowrap">
                    <Edit className="cell" type="date" disabled={!canEdit} value={b.start} onSave={(v) => patch({ start: v || b.start })} /> –{' '}
                    <Edit className="cell" type="date" disabled={!canEdit} value={b.end} onSave={(v) => patch({ end: v || b.end })} />{' '}
                    {canEdit && <Btn small title="Remove this blocked time" onClick={() => window.confirm(`Remove this blocked time${b.note ? ` (${b.note})` : ''}?`) && void save({ ...value, blocks: value.blocks.filter((_, k) => k !== i) })}>×</Btn>}
                  </span>
                }
              />
            );
          })}
        </Panel>
      </div>
    </div>
  );
}
